import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

export const BEIRUT_TIMEZONE = 'Asia/Beirut';
export const PERIOD_IDS = ['today', 'yesterday', '7d', 'month'];

export const HOUR_MS = 60 * 60 * 1000;

export const beirutDayKey = (date) => formatInTimeZone(date, BEIRUT_TIMEZONE, 'yyyy-MM-dd');

export const shiftDayKey = (dayKey, days) => {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

const firstDayOfMonthKey = (dayKey, monthOffset = 0) => {
  const [year, month] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1 + monthOffset, 1)).toISOString().slice(0, 10);
};

const lastDayOfMonthKey = (dayKey) => {
  const [year, month] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
};

/** First instant of a Beirut calendar day. */
export const startOfBeirutDay = (dayKey) => {
  let start = fromZonedTime(`${dayKey}T00:00:00`, BEIRUT_TIMEZONE);
  // Beirut starts DST at midnight, so that day has no 00:00 and date-fns-tz resolves it an hour early, on the previous day
  while (beirutDayKey(start) < dayKey) {
    start = new Date(start.getTime() + HOUR_MS);
  }
  return start;
};

/** Last millisecond of a Beirut calendar day. */
export const endOfBeirutDay = (dayKey) => new Date(startOfBeirutDay(shiftDayKey(dayKey, 1)).getTime() - 1);

const hourBuckets = (startMs, endMs) => {
  const buckets = [];
  for (let t = startMs; t < endMs; t += HOUR_MS) {
    buckets.push({ key: t, start: t, end: t + HOUR_MS, hour: Number(formatInTimeZone(t, BEIRUT_TIMEZONE, 'H')) });
  }
  return buckets;
};

const dayBuckets = (firstDay, lastDay) => {
  const buckets = [];
  let day = firstDay;
  let start = startOfBeirutDay(day).getTime();
  while (day <= lastDay) {
    const next = shiftDayKey(day, 1);
    const end = startOfBeirutDay(next).getTime();
    buckets.push({ key: day, start, end, day });
    day = next;
    start = end;
  }
  return buckets;
};

const buildRange = (firstDay, lastDay, bucket) => {
  const start = startOfBeirutDay(firstDay);
  const endExclusive = startOfBeirutDay(shiftDayKey(lastDay, 1)).getTime();
  return {
    firstDay,
    lastDay,
    bucket,
    start,
    end: new Date(endExclusive - 1),
    buckets: bucket === 'hour' ? hourBuckets(start.getTime(), endExclusive) : dayBuckets(firstDay, lastDay)
  };
};

/** Index of the bucket that contains `ms`, or -1 when it falls outside the range. */
export const findBucketIndex = (range, ms) => {
  const { buckets } = range;
  if (!buckets.length || ms < buckets[0].start || ms >= buckets[buckets.length - 1].end) return -1;
  if (range.bucket === 'hour') return Math.floor((ms - buckets[0].start) / HOUR_MS);

  let low = 0;
  let high = buckets.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (ms < buckets[mid].start) high = mid - 1;
    else if (ms >= buckets[mid].end) low = mid + 1;
    else return mid;
  }
  return -1;
};

/**
 * The selected period and the one it is compared with, both in Beirut time.
 *
 * `current.buckets` spans the whole period (a full day, 7 days or the whole month) so the chart axis
 * stays fixed; `current.nowIndex` is the bucket holding "now". `previous.cutoff` is the instant in
 * the previous period that lines up with "now", used for like-for-like comparisons.
 */
export const getPeriodWindows = (periodId, now = new Date()) => {
  const today = beirutDayKey(now);
  let current;
  let previous;

  if (periodId === 'yesterday') {
    const day = shiftDayKey(today, -1);
    current = buildRange(day, day, 'hour');
    previous = buildRange(shiftDayKey(day, -1), shiftDayKey(day, -1), 'hour');
  } else if (periodId === '7d') {
    current = buildRange(shiftDayKey(today, -6), today, 'day');
    previous = buildRange(shiftDayKey(today, -13), shiftDayKey(today, -7), 'day');
  } else if (periodId === 'month') {
    const monthStart = firstDayOfMonthKey(today);
    const previousMonthStart = firstDayOfMonthKey(today, -1);
    current = buildRange(monthStart, lastDayOfMonthKey(monthStart), 'day');
    previous = buildRange(previousMonthStart, lastDayOfMonthKey(previousMonthStart), 'day');
  } else {
    current = buildRange(today, today, 'hour');
    previous = buildRange(shiftDayKey(today, -1), shiftDayKey(today, -1), 'hour');
  }

  const nowMs = now.getTime();
  const currentEndExclusive = current.end.getTime() + 1;
  const isLive = nowMs < currentEndExclusive;
  const elapsed = Math.min(nowMs, currentEndExclusive) - current.start.getTime();

  return {
    id: PERIOD_IDS.includes(periodId) ? periodId : 'today',
    isLive,
    current: {
      ...current,
      nowIndex: isLive ? findBucketIndex(current, nowMs) : current.buckets.length - 1,
      fetchEnd: new Date(Math.min(current.end.getTime(), endOfBeirutDay(today).getTime()))
    },
    previous: {
      ...previous,
      cutoff: Math.min(previous.start.getTime() + elapsed, previous.end.getTime() + 1)
    }
  };
};

/** Start of the hour a record covers (records carry `date_hour` as a UTC `YYYY-MM-DD-HH`). */
export const recordTimestamp = (record) => {
  if (typeof record?.date_hour === 'string') {
    const [year, month, day, hour] = record.date_hour.split('-').map(Number);
    if (year && month && day) return Date.UTC(year, month - 1, day, Number.isFinite(hour) ? hour : 0);
  }
  const timestamp = Number(record?.date_hour_timestamp);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
};

/** Time span a record covers: one hour, or a whole Beirut day for the backend's daily rollups (ranges over 14 days). */
export const recordSpan = (record, range) => {
  const ms = recordTimestamp(record);
  if (ms === null) return null;
  if (!record.date) return [ms, ms + HOUR_MS];

  const index = range?.bucket === 'day' ? findBucketIndex(range, ms) : -1;
  if (index >= 0) return [range.buckets[index].start, range.buckets[index].end];
  return [startOfBeirutDay(record.date).getTime(), startOfBeirutDay(shiftDayKey(record.date, 1)).getTime()];
};

/**
 * Weight of each record up to `cutoff`: 1 when fully before it, 0 when after, pro-rated when the
 * cutoff falls inside the record's hour or day.
 */
export const cutoffWeight = (range, cutoff) => (record) => {
  const span = recordSpan(record, range);
  if (!span || cutoff >= span[1]) return 1;
  if (cutoff <= span[0]) return 0;
  return (cutoff - span[0]) / (span[1] - span[0]);
};
